import { useCallback } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getCurrencySymbol } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { CurrencyCode } from "web_myplann/Shared/constants";

type TranslateFunction = ReturnType<typeof useResource>["t"];

type FormatGoalAmountOptions = {
	withSymbol?: boolean;
};

const localeFor = (currencyCode?: CurrencyCode | string | null): string => {
	switch (currencyCode) {
		case CurrencyCode.EUR:
			return "sk-SK";
		case CurrencyCode.CHF:
			return "fr-CH";
		case CurrencyCode.GBP:
			return "en-GB";
		case CurrencyCode.USD:
			return "en-US";
		case CurrencyCode.PLN:
			return "pl-PL";
		default:
			return "cs-CZ";
	}
};

export const formatGoalAmount = (
	amount: number,
	currencyCode: CurrencyCode | string | null | undefined,
	t: TranslateFunction,
	{ withSymbol = true }: FormatGoalAmountOptions = {},
): string => {
	const locale = localeFor(currencyCode);
	const symbol =
		currencyCode && withSymbol
			? (getCurrencySymbol(currencyCode as CurrencyCode) ?? "")
			: "";
	const appendSymbol = (value: string) =>
		symbol ? `${value} ${symbol}` : value;

	if (amount >= 1_000_000) {
		const mil = amount / 1_000_000;
		const formatted =
			mil % 1 === 0
				? mil.toFixed(0)
				: new Intl.NumberFormat(locale, {
						maximumFractionDigits: 1,
						minimumFractionDigits: 1,
					}).format(mil);
		return appendSymbol(`${formatted} ${t(Resources.Goals.Amount.millions)}`);
	}

	if (amount >= 100_000) {
		const tis = Math.round(amount / 1000);
		return appendSymbol(`${tis} ${t(Resources.Goals.Amount.thousands)}`);
	}

	return appendSymbol(amount.toLocaleString(locale));
};

export const useFormatGoalAmount = () => {
	const { t } = useResource();

	return useCallback(
		(
			amount: number,
			currencyCode?: CurrencyCode | string | null,
			options?: FormatGoalAmountOptions,
		): string => formatGoalAmount(amount, currencyCode, t, options),
		[t],
	);
};
